import { useChainId, useWaitForTransactionReceipt, useWriteContract } from 'wagmi'
import { keccak256, toBytes } from 'viem'
import { referralManagerAbi } from '@/abi/referralManagerAbi'
import { operatorAbi } from '@/abi/operatorAbi'
import { getContractAddress } from '@/config/contracts'

const OPERATOR_ROLE = keccak256(toBytes('OPERATOR_ROLE'))

// Referral lifecycle + rate administration on the currently-connected chain, signed by the
// operator's own wallet. Registration/lifecycle/clawback require the ReferralManager's
// OPERATOR_ROLE; rate setters require TREASURY_ROLE. Modeled on `useOperatorRoleMutations`:
// one `useWriteContract` drives every call, `useWaitForTransactionReceipt` surfaces
// confirmation state for the toast pattern.
export function useReferralAdminMutations() {
    const chainId = useChainId()
    const manager = getContractAddress(chainId, 'referralManager')
    const operator = getContractAddress(chainId, 'operator')
    const { writeContract, data: hash, isPending, error, reset } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    function call(functionName: string, args: unknown[]) {
        if (!manager) return
        writeContract({
            address: manager,
            abi: referralManagerAbi,
            functionName,
            args,
        } as Parameters<typeof writeContract>[0])
    }

    return {
        register: (referrer: `0x${string}`, code: string, shareBpsOverride: number) =>
            call('registerReferrer', [referrer, code, shareBpsOverride]),
        suspend: (referrer: `0x${string}`) => call('suspendReferrer', [referrer]),
        reactivate: (referrer: `0x${string}`) => call('reactivateReferrer', [referrer]),
        terminate: (referrer: `0x${string}`) => call('terminateReferrer', [referrer]),
        clawback: (referrer: `0x${string}`) => call('clawback', [referrer]),
        setDefaultShareBps: (bps: number) => call('setDefaultShareBps', [bps]),
        setReferrerShareBps: (referrer: `0x${string}`, bps: number) =>
            call('setReferrerShareBps', [referrer, bps]),
        // Preflight remediations: fix the two deploy-time gaps hosted testnets can
        // ship with - the RM operator role held only by the deployer key, and an unwired operator.
        grantOperatorRole: (grantee: `0x${string}`) => call('grantRole', [OPERATOR_ROLE, grantee]),
        wireReferralManager: () => {
            if (!operator || !manager) return
            writeContract({
                address: operator,
                abi: operatorAbi,
                functionName: 'setReferralManager',
                args: [manager],
            } as Parameters<typeof writeContract>[0])
        },
        hash,
        isPending,
        isConfirming,
        isSuccess,
        error,
        reset,
    }
}
